import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../config/firebase';
import { collection, addDoc, Timestamp } from 'firebase/firestore';
import { Auction, AuctionLot } from '../types/auction.types';
import toast from 'react-hot-toast';

const CreateAuction: React.FC = () => {
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  // Auction Details
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [location, setLocation] = useState('');

  // CSV Import
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [parsedLots, setParsedLots] = useState<AuctionLot[]>([]);
  const [previewData, setPreviewData] = useState<any[]>([]);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }

    setCsvFile(file);
    parseCSV(file);
  };

  const parseCSV = (file: File) => {
    const reader = new FileReader();
    
    reader.onload = (event) => {
      const text = event.target?.result as string;
      const lines = text.split('\n');
      const headers = lines[0].split(',').map(h => h.trim());
      
      const data: any[] = [];
      const lots: AuctionLot[] = [];

      for (let i = 1; i < lines.length; i++) {
        if (!lines[i].trim()) continue;

        const values = lines[i].split(',').map(v => v.trim());
        const row: any = {};
        
        headers.forEach((header, index) => {
          row[header] = values[index] || '';
        });

        data.push(row);

        // Create lot object
        const sku = row['SKU'] || row['sku'];
        const lot: AuctionLot = {
          id: `lot_${Date.now()}_${i}`,
          sku: sku,
          title: row['Title'] || row['title'] || '',
          description: row['Description'] || row['description'] || '',
          startingBid: parseFloat(row['Starting Bid'] || row['startingBid'] || '0'),
          reservePrice: row['Reserve Price'] ? parseFloat(row['Reserve Price']) : undefined,
          estimatedValue: row['Estimated Value'] || row['estimatedValue'],
          category: row['Category'] || row['category'] || 'Uncategorized',
          condition: row['Condition'] || row['condition'],
          year: row['Year'] || row['year'],
          manufacturer: row['Manufacturer'] || row['manufacturer'],
          images: generateImageUrls(sku),
          bidCount: 0,
          status: 'pending',
          createdAt: new Date()
        };

        lots.push(lot);
      }

      setPreviewData(data);
      setParsedLots(lots);
      toast.success(`Parsed ${lots.length} lots from CSV`);
    };

    reader.onerror = () => {
      toast.error('Error reading CSV file');
    };

    reader.readAsText(file);
  };

  const generateImageUrls = (sku: string): string[] => {
    // For now, generate placeholder image paths
    // In production, you'd upload these to Firebase Storage
    const images: string[] = [];
    for (let i = 1; i <= 3; i++) {
      images.push(`/images/${sku}_${i.toString().padStart(2, '0')}.jpg`);
    }
    return images;
  };

  const handleCreateAuction = async () => {
  if (!title || !startDate || !endDate) {
    toast.error('Please fill in auction title and dates');
    return;
  }

  if (parsedLots.length === 0) {
    toast.error('Please upload and parse a CSV file with lots');
    return;
  }

  if (!userProfile?.auctionHouseId) {
    toast.error('Auction house information not found');
    return;
  }

  setLoading(true);

  try {
    // Clean the lots data - remove any undefined values
    const cleanedLots = parsedLots.map(lot => ({
      id: lot.id,
      sku: lot.sku || '',
      title: lot.title || '',
      description: lot.description || '',
      startingBid: lot.startingBid || 0,
      reservePrice: lot.reservePrice || null,
      estimatedValue: lot.estimatedValue || null,
      category: lot.category || 'Uncategorized',
      condition: lot.condition || null,
      year: lot.year || null,
      manufacturer: lot.manufacturer || null,
      images: lot.images || [],
      currentBid: lot.currentBid || null,
      bidCount: lot.bidCount || 0,
      status: lot.status || 'pending',
      createdAt: new Date()
    }));

    const auction = {
      auctionHouseId: userProfile.auctionHouseId,
      auctionHouseName: userProfile.auctionHouseName || 'Unknown',
      title: title,
      description: description || null,
      startDate: new Date(startDate),
      endDate: new Date(endDate),
      location: location || null,
      status: 'draft',
      lots: cleanedLots,
      totalLots: cleanedLots.length,
      createdBy: userProfile.uid,
      createdAt: new Date(),
      updatedAt: new Date()
    };

    console.log('Creating auction with data:', auction);

    const docRef = await addDoc(collection(db, 'auctions'), auction);
    
    console.log('Auction created with ID:', docRef.id);
    toast.success('Auction created successfully!');
    navigate('/auctioneer/dashboard');
  } catch (error: any) {
    console.error('Error creating auction:', error);
    console.error('Error code:', error.code);
    console.error('Error message:', error.message);
    toast.error('Failed to create auction: ' + error.message);
  } finally {
    setLoading(false);
  }
};

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Create New Auction</h1>
        <button
          onClick={() => navigate('/auctioneer/dashboard')}
          className="btn-secondary"
        >
          Cancel
        </button>
      </div>

      {/* Auction Details Form */}
      <div className="card">
        <h2 className="text-xl font-bold mb-4">Auction Details</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Auction Title *
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              placeholder="12-07-25 HiBid Coins n Sports Cards Sale"
              disabled={loading}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              rows={3}
              placeholder="Auction description..."
              disabled={loading}
            />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Start Date & Time *
              </label>
              <input
                type="datetime-local"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                disabled={loading}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                End Date & Time *
              </label>
              <input
                type="datetime-local"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                disabled={loading}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Location
            </label>
            <input
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              placeholder="Online / Physical Address"
              disabled={loading}
            />
          </div>
        </div>
      </div>

      {/* CSV Upload */}
      <div className="card">
        <h2 className="text-xl font-bold mb-4">Import Lots (CSV)</h2>
        
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Upload CSV File
          </label>
          <input
            type="file"
            accept=".csv"
            onChange={handleFileUpload}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            disabled={loading}
          />
          <p className="text-sm text-gray-600 mt-2">
            CSV should include: SKU, Title, Description, Starting Bid, Reserve Price, 
            Estimated Value, Category, Condition, Year, Manufacturer
          </p>
        </div>

        {previewData.length > 0 && (
          <div>
            <h3 className="font-semibold mb-2">
              Preview: {parsedLots.length} lots loaded
            </h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">SKU</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Starting Bid</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {parsedLots.slice(0, 5).map((lot, index) => (
                    <tr key={index}>
                      <td className="px-4 py-2 text-sm">{lot.sku}</td>
                      <td className="px-4 py-2 text-sm">{lot.title}</td>
                      <td className="px-4 py-2 text-sm">${lot.startingBid}</td>
                      <td className="px-4 py-2 text-sm">{lot.category}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {parsedLots.length > 5 && (
                <p className="text-sm text-gray-600 mt-2 text-center">
                  Showing 5 of {parsedLots.length} lots...
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end space-x-4">
        <button
          onClick={() => navigate('/auctioneer/dashboard')}
          className="btn-secondary"
          disabled={loading}
        >
          Cancel
        </button>
        <button
          onClick={handleCreateAuction}
          disabled={loading || parsedLots.length === 0}
          className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Creating Auction...' : 'Create Auction'}
        </button>
      </div>
    </div>
  );
};

export default CreateAuction;